// Kompaktowy formularz dodawania pokoi wzorowany na AccommodationSection

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useAccommodation } from "../../AccommodationProvider";
import {
  AssignmentType,
  MinAvailabilityMap,
} from "../../types/accommodation.types";
import { buildOccupancyOptions } from "../../utils/occupancyOptions";
import {
  calculateNightPriceWithMarkup,
  MarkupSettings,
} from "../../../../utils/markupCalculations";
import {
  NightBasePrices,
  toNightBasePrices,
} from "../../utils/markupRecalculation";

interface InlineRoomFormProps {
  categoryOptionId: string;
  room: MinAvailabilityMap[string];
  availableCount: number;
  maxPersonsPerRoom: number;
  accommodationDays: any[];
  markup: MarkupSettings | null;
  onClose: () => void;
}

const InlineRoomForm: React.FC<InlineRoomFormProps> = ({
  categoryOptionId,
  room,
  availableCount,
  maxPersonsPerRoom,
  accommodationDays,
  markup,
  onClose,
}) => {
  const { guestStructure, addRoom, addToAllDays, currentDayIndex } =
    useAccommodation();

  // Stan formularza
  const [assignmentType, setAssignmentType] = useState<AssignmentType>("pairs");
  const [quantity, setQuantity] = useState(1);
  const [dailyPrices, setDailyPrices] = useState<{ [dayId: string]: number }>(
    {}
  );
  const [personsCount, setPersonsCount] = useState(2);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Opcje przypisania na podstawie struktury gości
  const assignmentOptions = useMemo(
    () => buildOccupancyOptions(guestStructure, maxPersonsPerRoom),
    [guestStructure, maxPersonsPerRoom]
  );

  // Filtruj dostępne opcje
  const availableOptions = assignmentOptions.filter((opt) => opt.available);

  // Dzień, na który dodajemy pokój w trybie "tylko aktualny dzień"
  const currentDay = accommodationDays[currentDayIndex];
  const currentDayPrice = currentDay ? dailyPrices[currentDay.id] || 0 : 0;

  // Funkcja pobierania sugerowanej ceny (netto z markupem)
  const isMarkupNull = markup === null;
  const getSuggestedPrice = useCallback((
    assignmentType: AssignmentType,
    day: any,
    personsCount?: number
  ): number => {
    // Spróbuj różnych formatów daty
    let dateString = day.date;
    let pricing = room.pricing[dateString];

    // Jeśli nie ma pricing, spróbuj z formatem ISO date
    if (!pricing && day.date) {
      const isoDate = day.date.split("T")[0]; // "2025-10-13T00:00:00.000Z" -> "2025-10-13"
      pricing = room.pricing[isoDate];
      if (pricing) dateString = isoDate;
    }

    if (!pricing) {
      return 0;
    }

    let basePrice = 0;
    const actualPersonsCount = personsCount || maxPersonsPerRoom;

    switch (assignmentType) {
      case "pairs":
        basePrice = pricing.double?.net || 0;
        break;
      case "singles_shared":
        if (actualPersonsCount >= 3 && pricing.triple) {
          basePrice = pricing.triple.net;
        } else if (actualPersonsCount >= 2 && pricing.double) {
          basePrice = pricing.double.net;
        } else {
          basePrice = pricing.single?.net || 0;
        }
        break;
      case "singles_alone":
        basePrice = pricing.single?.net || 0;
        break;
      default:
        break;
    }

    // Aplikuj narzut i dopłatę weekendową (wg daty tej nocy)
    return calculateNightPriceWithMarkup(basePrice, day.date, markup);
    // Depend on markup values, not its reference: a new object with the same
    // values must not re-run the price effect and discard manually typed prices.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    room.pricing,
    maxPersonsPerRoom,
    markup?.accommodationMarkupType,
    markup?.accommodationMarkupValue,
    markup?.weekendSurchargePercent,
    isMarkupNull,
  ]);

  // Inicjalizuj ceny dzienne przy zmianie opcji
  useEffect(() => {
    const newDailyPrices: { [dayId: string]: number } = {};

    accommodationDays.forEach((day) => {
      const suggestedPrice = getSuggestedPrice(
        assignmentType,
        day,
        personsCount
      );
      newDailyPrices[day.id] = suggestedPrice;
    });

    setDailyPrices(newDailyPrices);
  }, [assignmentType, personsCount, accommodationDays, room.pricing, getSuggestedPrice]);

  // Automatycznie ustaw pierwszą dostępną opcję
  useEffect(() => {
    if (
      availableOptions.length > 0 &&
      !availableOptions.find((opt) => opt.value === assignmentType)
    ) {
      setAssignmentType(availableOptions[0].value);
      setPersonsCount(availableOptions[0].personsPerRoom);
    }
  }, [availableOptions, assignmentType, assignmentOptions]);

  // Aktualizuj personsCount przy zmianie assignmentType
  useEffect(() => {
    const currentOption = assignmentOptions.find(
      (opt) => opt.value === assignmentType
    );
    if (currentOption) {
      setPersonsCount(currentOption.personsPerRoom);
    }
  }, [assignmentType, assignmentOptions]);

  // Walidacja maksymalnej ilości - ZMIANA: Usuń ograniczenie przy overbookingu
  const currentOption = assignmentOptions.find(
    (opt) => opt.value === assignmentType
  );

  // Stawki bazowe netto nocy (te same dane co cena sugerowana)
  const getNightBasePrices = (day: any): NightBasePrices | null => {
    const isoDate = (day.date || "").split("T")[0];
    return toNightBasePrices(room.pricing[day.date] ?? room.pricing[isoDate]);
  };

  // Handle dodawania pokoju - ZMIANA: Usuń walidację maxQuantity
  const handleSubmit = async () => {
    if (!currentOption || quantity <= 0) return;

    setIsSubmitting(true);

    try {
      const avgPrice = addToAllDays
        ? Object.values(dailyPrices).reduce((sum, price) => sum + price, 0) /
          Object.values(dailyPrices).length
        : currentDayPrice;

      // Cena wyliczona i stawki bazowe każdej nocy: pozwalają później przeliczyć
      // pokój po zmianie narzutu i odróżnić cenę wpisaną ręcznie
      const dailySuggestedPrices: { [dayId: string]: number } = {};
      const dailyBasePrices: { [dayId: string]: NightBasePrices } = {};
      accommodationDays.forEach((day) => {
        const basePrices = getNightBasePrices(day);
        if (!basePrices) return;
        dailyBasePrices[day.id] = basePrices;
        dailySuggestedPrices[day.id] = getSuggestedPrice(
          assignmentType,
          day,
          personsCount
        );
      });
      const currentDayId = currentDay?.id;

      await addRoom({
        categoryOptionId,
        assignmentType,
        quantity,
        pricePerRoom: avgPrice,
        dailyPrices: addToAllDays ? dailyPrices : undefined,
        ...(addToAllDays
          ? { dailySuggestedPrices, dailyBasePrices }
          : currentDayId && dailyBasePrices[currentDayId]
            ? {
                suggestedPrice: dailySuggestedPrices[currentDayId],
                basePrices: dailyBasePrices[currentDayId],
              }
            : {}),
        personsPerRoom:
          assignmentType === "singles_shared"
            ? personsCount
            : currentOption.personsPerRoom,
      });

      onClose();
    } catch (error) {
      console.error("Failed to add room:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (availableOptions.length === 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded p-3 m-3">
        <div className="text-center">
          <p className="text-sm text-yellow-700 mb-2">
            Ustaw strukturę gości w panelu kontrolnym
          </p>
          <button
            onClick={onClose}
            className="text-xs text-yellow-600 hover:text-yellow-800"
          >
            Zamknij
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 border-t border-gray-200 p-4">
      <div className="max-w-4xl">
        <div className="text-sm text-gray-700 mb-3">
          {addToAllDays
            ? `Dodawanie na wszystkie ${accommodationDays.length} dni jednocześnie`
            : "Dodawanie na aktualny dzień"}
        </div>

        <div className="space-y-3">
          {/* Pierwszy wiersz: Podstawowe kontrolki */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Dla kogo:
              </label>
              <select
                value={assignmentType}
                onChange={(e) => {
                  const newType = e.target.value as AssignmentType;
                  setAssignmentType(newType);
                }}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              >
                {availableOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Pole osób na pokój (tylko dla singles_shared) */}
            {assignmentType === "singles_shared" ? (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Osób na pokój:
                </label>
                <input
                  type="number"
                  min="1"
                  value={personsCount}
                  onChange={(e) =>
                    setPersonsCount(Math.max(1, parseInt(e.target.value) || 1))
                  }
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                />
              </div>
            ) : (
              <div></div>
            )}

            <div>
              <label
                className={`block text-xs font-medium mb-1 ${
                  quantity > availableCount
                    ? "text-orange-500"
                    : "text-gray-700"
                }`}
              >
                Ilość pokoi
                {availableCount < 0
                  ? " (⚠️ overbooking)"
                  : ` (dostępne: ${availableCount})`}
                :
              </label>
              <input
                type="number"
                min="1"
                value={quantity}
                onChange={(e) =>
                  setQuantity(Math.max(1, parseInt(e.target.value) || 1))
                }
                className={`w-full px-2 py-1 text-sm border rounded focus:ring-1 focus:ring-blue-500 ${
                  availableCount < 0
                    ? "border-yellow-400 bg-yellow-50"
                    : "border-gray-300"
                }`}
                title={
                  availableCount < 0
                    ? `Ostrzeżenie: Dostępnych jest tylko ${room.minCount} pokoi, ale możesz dodać więcej (overbooking)`
                    : `Dostępnych pokoi: ${availableCount}`
                }
              />
            </div>
          </div>

          {/* Sekcja cen - tylko gdy potrzebna */}
          {(addToAllDays ||
            (!addToAllDays && accommodationDays.length > 0)) && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">
                {addToAllDays
                  ? "Ceny na każdy dzień:"
                  : "Cena na aktualny dzień:"}
              </label>

              {addToAllDays ? (
                <div className="space-y-2">
                  {accommodationDays.map((day, index) => {
                    const date = new Date(day.date);
                    const dayName = date.toLocaleDateString("pl-PL", {
                      weekday: "short",
                    });
                    const dayMonth = date.toLocaleDateString("pl-PL", {
                      day: "2-digit",
                      month: "2-digit",
                    });

                    return (
                      <div key={day.id} className="flex items-center gap-2">
                        <span className="text-xs text-gray-600 w-20">
                          {dayMonth} ({dayName}):
                        </span>
                        <input
                          type="number"
                          min="0"
                          value={dailyPrices[day.id] || 0}
                          onChange={(e) =>
                            setDailyPrices((prev) => ({
                              ...prev,
                              [day.id]: parseInt(e.target.value) || 0,
                            }))
                          }
                          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                          placeholder="0"
                        />
                        <span className="text-xs text-gray-500">PLN</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-600">Cena:</span>
                  <input
                    type="number"
                    min="0"
                    value={currentDayPrice}
                    onChange={(e) => {
                      const price = parseInt(e.target.value) || 0;
                      if (currentDay) {
                        setDailyPrices((prev) => ({
                          ...prev,
                          [currentDay.id]: price,
                        }));
                      }
                    }}
                    className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                    placeholder="0"
                  />
                  <span className="text-xs text-gray-500">PLN</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Przyciski i podsumowanie */}
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-200">
          <div className="flex items-center gap-4">
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || quantity <= 0}
              className={`px-4 py-2 text-sm font-medium rounded transition-colors ${
                isSubmitting || quantity <= 0
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : availableCount < 0
                    ? "bg-yellow-500 text-white hover:bg-yellow-600"
                    : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
              title={
                availableCount < 0
                  ? "Dodawanie pokoi spowoduje overbooking"
                  : "Dodaj pokoje"
              }
            >
              {isSubmitting
                ? "Dodaję..."
                : availableCount < 0
                  ? "⚠️ Dodaj (overbooking)"
                  : "➕ Dodaj pokoje"}
            </button>

            <button
              onClick={onClose}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              Anuluj
            </button>
          </div>

          {/* Podsumowanie */}
          {currentOption && quantity > 0 && (
            <div className="text-sm text-gray-600">
              <span className="font-medium">
                👥{" "}
                {quantity *
                  (assignmentType === "singles_shared"
                    ? personsCount
                    : currentOption.personsPerRoom)}{" "}
                os.
              </span>
              <span className="mx-2">|</span>
              <span className="font-medium">
                💰{" "}
                {addToAllDays
                  ? Math.round(
                      Object.values(dailyPrices).reduce(
                        (sum, price) => sum + price,
                        0
                      ) / Math.max(1, Object.values(dailyPrices).length)
                    )
                  : currentDayPrice}{" "}
                PLN/pok. (śr.)
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InlineRoomForm;
